import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiRouteConstants } from '../../../../../../core/constants/api-routes';
import { AuthorizeNetPaymentTransactionDetailsResponse } from '../../../../../../core/models/commerce/payment/authorize-net-payment-transaction-details-response';
import { AuthorizeNetPaymentTransactionStatusResponse } from '../../../../../../core/models/commerce/payment/authorize-net-payment-transaction-status-response';
import { CustomerPaymentDashboardGridResponse } from '../../../customer-payment-dashboard/models/customer-payment-dashboard-grid-response';
import { ConfirmPaymentRequest } from '../../../payment-details/models/confirm-payment-request';
import { CreatePaymentRequest } from '../../../payment-details/models/create-payment-request';
import { CreatePaymentResponse } from '../../../payment-details/models/create-payment-response';
import { CreditInformationResponse } from '../../../payment-details/models/credit-information-response';
import { CreditPaymentRequest } from '../../../payment-details/models/credit-payment-request';
import { PaymentDetailsResponse } from '../../../payment-details/models/payment-details-response';
import { PaymentDetailsUpdateRequest } from '../../../payment-details/models/payment-details-update-request';

@Injectable({
    providedIn: 'root',
})
export class PaymentService {
    private readonly paymentApiRoute = ApiRouteConstants.Payment;

    constructor(private readonly http: HttpClient) {}

    get(orderId: string): Observable<PaymentDetailsResponse> {
        return this.http.get<PaymentDetailsResponse>(
            this.paymentApiRoute.getPaymentDetailsUrl(orderId),
        );
    }

    getDetails(
        orderId: string,
        paymentId: string,
    ): Observable<AuthorizeNetPaymentTransactionDetailsResponse> {
        return this.http.get<AuthorizeNetPaymentTransactionDetailsResponse>(
            this.paymentApiRoute.getPaymentTransactionDetailsUrl(
                orderId,
                paymentId,
            ),
        );
    }

    getStatus(
        orderId: string,
        paymentId: string,
    ): Observable<AuthorizeNetPaymentTransactionStatusResponse> {
        return this.http.get<AuthorizeNetPaymentTransactionStatusResponse>(
            this.paymentApiRoute.getPaymentTransactionStatusUrl(
                orderId,
                paymentId,
            ),
        );
    }

    getAllForCustomer(
        userId: number,
    ): Observable<CustomerPaymentDashboardGridResponse[]> {
        return this.http.get<CustomerPaymentDashboardGridResponse[]>(
            this.paymentApiRoute.getUserPaymentsUrl(userId),
        );
    }

    save(
        orderId: string,
        request: PaymentDetailsUpdateRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.paymentApiRoute.getPaymentDetailsUrl(orderId),
            request,
        );
    }

    create(
        orderId: string,
        request: CreatePaymentRequest,
    ): Observable<CreatePaymentResponse> {
        return this.http.post<CreatePaymentResponse>(
            this.paymentApiRoute.getPaymentsUrl(orderId),
            request,
        );
    }

    void(orderId: string, paymentId: string): Observable<void> {
        return this.http.post<void>(
            this.paymentApiRoute.voidPaymentUrl(orderId, paymentId),
            {},
        );
    }

    getCreditInformation(
        orderId: string,
        paymentId: string,
    ): Observable<CreditInformationResponse> {
        return this.http.get<CreditInformationResponse>(
            this.paymentApiRoute.creditPaymentUrl(orderId, paymentId),
        );
    }

    credit(
        orderId: string,
        paymentId: string,
        request: CreditPaymentRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.paymentApiRoute.creditPaymentUrl(orderId, paymentId),
            request,
        );
    }

    confirm(
        orderId: string,
        paymentId: string,
        request: ConfirmPaymentRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.paymentApiRoute.confirmPaymentUrl(orderId, paymentId),
            request,
        );
    }
}
